import { jsx as _jsx, Fragment as _Fragment, jsxs as _jsxs } from "react/jsx-runtime";
import React from "react";
import { useUXPContext } from "@core/context/useUXPContext";
import { Loading } from '@widget/loaders';
import useDataList from '../../../hooks/useDataList';
import Icon from "@icon/Icon";
import classNames from 'classnames';
import NotificationBlock from '../../helper/NotificationBlock';
import ButtonComponent from "@widget/buttons/ButtonComponent";
const DataListBase = React.forwardRef((props, ref) => {
    const context = useUXPContext();
    let { data, pageSize, renderItem, renderLoading, className, args, showFooter = true, scrollStep = 5, showEndOfContent = true, onItemsLoad, renderNoItems } = props;
    let [currentKey, setCurrentKey] = React.useState(0);
    let [showUp, setShowUp] = React.useState(false);
    let [showDown, setShowDown] = React.useState(false);
    let [showGoUp, setShowGoUp] = React.useState(false);
    // search list params 
    let [param, setParams] = React.useState({
        page: 0,
        lastPageToken: null,
        args: args
    });
    // ref 
    let lastPageToken = React.useRef(null);
    let timer = null;
    // if arguments has changed 
    //reset page count & page token
    React.useEffect(() => {
        lastPageToken.current = null;
        setParams({
            page: 0,
            lastPageToken: null,
            args: args
        });
    }, [args, data]);
    // use data list
    let { items, pageToken, hasMore, loading, error, total, removeItem, updateItem } = useDataList(data, pageSize, param.page, param.lastPageToken, param.args);
    React.useEffect(() => {
        let _total = total;
        if (!_total)
            _total = items.length;
        if (onItemsLoad) {
            onItemsLoad(_total, items.length, items);
        }
    }, [total, items]);
    // update last page token
    React.useEffect(() => {
        lastPageToken.current = pageToken;
    }, [pageToken]);
    React.useImperativeHandle(ref, () => ({
        removeItem: removeItem,
        updateItem: updateItem
    }));
    // refs
    const observer = React.useRef(null);
    const listContainerRef = React.useRef(null);
    const listRef = React.useRef(null);
    const listItemsRef = React.useRef([]);
    // last ref 
    const lastItemRef = React.useCallback((node) => {
        if (loading)
            return;
        if (observer.current)
            observer.current.disconnect();
        observer.current = new IntersectionObserver((entries) => {
            if (entries[0].isIntersecting && hasMore) {
                setParams(prev => {
                    let updated = Object.assign({}, prev);
                    updated.page = updated.page + 1;
                    updated.lastPageToken = lastPageToken.current;
                    // fallback 
                    if (updated.page > 0 && updated.lastPageToken == null)
                        updated.lastPageToken = items.length.toString();
                    return updated;
                });
            }
        });
        if (node)
            observer.current.observe(node);
    }, [loading, hasMore]);
    React.useEffect(() => {
        toggleFooter();
    }, [currentKey]);
    // toggle footer 
    const toggleFooter = () => {
        let _showUp = false;
        let _showDown = true;
        let _showGoUP = false;
        if (currentKey > 1)
            _showUp = true;
        if (!hasMore && currentKey > 0 && currentKey >= items.length - 3 && currentKey <= items.length - 1) {
            // _showDown = false
            _showGoUP = true;
        }
        setShowUp(_showUp);
        setShowDown(_showDown);
        setShowGoUp(_showGoUP);
    };
    // scroll
    const scrollItemToView = (key) => {
        let scrollTop = 0;
        items.map((item, index) => {
            if (index < key) {
                let nextItemDetails = listItemsRef.current[index].getBoundingClientRect();
                scrollTop += nextItemDetails.height;
            }
        });
        listRef.current.scrollTo({
            top: scrollTop,
            behavior: 'smooth'
        });
    };
    // scroll on click
    const scrollOnclick = (direction) => {
        let _currentKey = currentKey;
        if (direction == "up") {
            _currentKey -= scrollStep;
            if (_currentKey < 0)
                _currentKey = 0;
        }
        else {
            _currentKey += scrollStep;
            if (_currentKey >= (items.length - 1)) {
                _currentKey = (items.length - 1);
                if (hasMore)
                    _currentKey = (items.length - 2);
            }
        }
        setCurrentKey(_currentKey);
        scrollItemToView(_currentKey);
    };
    // on scroll handler
    const onScrollHandler = () => {
        if (timer) {
            clearTimeout(timer);
        }
        timer = setTimeout(() => {
            let listContainerDetails = listContainerRef.current.getBoundingClientRect();
            items.map((item, key) => {
                if (!(hasMore && items.length - 1 === key)) {
                    let element = listItemsRef.current[key];
                    let boundDetails = element.getBoundingClientRect();
                    if (boundDetails.top >= listContainerDetails.top + (boundDetails.height * 0.5) && boundDetails.top <= listContainerDetails.top + (boundDetails.height * 1.5)) {
                        setCurrentKey(key);
                    }
                }
            });
        }, 200);
    };
    //return
    return (_jsxs("div", { className: classNames("uxpcore_datalist__container data-list-container", className, { "has-footer": showFooter }), ref: listContainerRef, onScroll: onScrollHandler, children: [_jsxs("div", { className: "uxpcore_datalist uxpcore_datalist__list data-list", ref: listRef, children: [items.map((item, key) => {
                        let itemRef = (el) => (listItemsRef.current[key] = el);
                        if (key == (items.length - 1) && hasMore)
                            itemRef = lastItemRef;
                        return (_jsx("div", { className: "uxpcore_datalist__item data-list-item", ref: itemRef, children: renderItem(item, key) }, key));
                    }), items.length == 0 && _jsx(_Fragment, { children: renderNoItems
                            ? renderNoItems()
                            : _jsx(NotificationBlock, { message: context.$L('uxp-core.text.no-items-found'), class: "uxpcore_notification--no-items-found" }) }), loading && _jsxs("div", { className: "uxpcore_datalist__loading dl-loading-block", children: [" ", renderLoading ? renderLoading() : _jsx(Loading, {}), " "] }), !hasMore && !error && showEndOfContent && items.length > 0 && _jsx(NotificationBlock, { message: `-- ${context.$L('uxp-core.text.end-of-content')} --`, class: "uxpcore_notification--end-of-content" })] }), showFooter &&
                _jsxs("div", { className: classNames("uxpcore_datalist__footer data-list-footer", { "has-both": showDown && showUp }), children: [showUp &&
                            _jsx("div", { className: "uxpcore_datalist__scroll-btn uxpcore_datalist__scroll-btn--up scroll-btn scroll-up", onClick: () => scrollOnclick("up"), children: _jsx(Icon, { icon: ['fas', 'chevron-up'] }) }), showDown &&
                            _jsx("div", { className: "uxpcore_datalist__scroll-btn uxpcore_datalist__scroll-btn--down scroll-btn scroll-down", onClick: () => scrollOnclick("down"), children: _jsx(Icon, { icon: ['fas', 'chevron-down'] }) }), showGoUp &&
                            _jsx("div", { className: "uxpcore_datalist__go-up-btn-container go-up-btn-container", children: _jsx(ButtonComponent, { title: context.$L('uxp-core.text.back-to-top'), onClick: () => {
                                        setCurrentKey(0);
                                        scrollItemToView(0);
                                    }, variant: 'secondary' }) })] })] }));
});
/**
 *
 * A infinite-scrollable list that supports paging in of items
 * @export
 *
 *
 *
 *
 * @example
 * ```
 *  Using array
 *
 *  let data:any[] = [
 *      {
 *          "id": 1,
 *          "name": "Name 01"
 *      },
 *      {
 *          "id": 2,
 *          "name": "Name 01"
 *      },
 *      {
 *          "id": 3,
 *          "name": "Name 01"
 *      }
 *  ]
 *
 *  function renderItem(item:any, key:number) {
 *      return <div>
 *          <div>{item.name} </div>
 *      </div>
 *  }
 *
 *  <DataList
 *      data={data}
 *      renderItem={(item, key) => renderItem(item, key)}
 *      pageSize={10}
 *  />
 * ```
 *
 * @example
 * ```
 *  Using IDataFunction
 *  async function getData(max:number, last: string, args: any) {
 *      if(!last) last = "0";
 *
 *      return new Promise<{items:any[], pageToken: string}>((done, nope) =>{
 *          executeAction("model", "action", {max: max, last: last, args: args})
 *          .then(res => {
 *              done({items: res.data, pageToken: res.lastPageToken })
 *          })
 *          .catch(e => {
 *              done({items: [], pageToken: last})
 *          })
 *      })
 *  }
 *
 *  function renderItem(item:any, key:number) {
 *      return <div>
 *          <div>{item.name} </div>
 *      </div>
 *  }
 *
 *  <DataList
 *      data={(max, last, args) => getData(max, last, args)}
 *      renderItem={(item, key) => renderItem(item, key)}
 *      pageSize={10}
 *  />
 *
 * ```
 *
 * @example
 * ```
 *  Using other props
 *
 *  <DataList
 *      data={data}
 *      renderItem={(item, key) => renderItem(item, key)}
 *      pageSize={10}
 *      className="custom-css-class"
 *      showFooter={false}
 *      showEndOfContent={false}
 *  />
 *
 * ```
 *
 * @deprecated Use ObjectSearchComponent with one column (`renderColumn` draws the row, no `label`), `paging="infinite"`
 *   and `hidePaging`. DataList keeps working; it gets no new features. See docs/v5/deprecated-components.md.
 * @category Data Display/Lists
 */
const DataList = React.memo(DataListBase);
export default DataList;
